import { Loader } from '@/components/Loader';
import { Button, Text } from '@/components/ui';
import { Select, SelectOption } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ContractCard } from './components/ContractCard';
import { useState, useEffect } from 'react';
import { CreateContractModal } from './components/CreateContractModal';
import { useContracts } from '@/hooks/useContracts';
import { useCompanies } from '@/hooks/useCompanies';
import { Contract } from '@console/utils';
import { UpdateContractModal } from './components/UpdateContractModal';
import { RenewContractModal } from './components/RenewContractModal';
import { DatabaseContract } from '@/lib/api/movidesk';
import { SyncContractsModal } from './components/SyncContractsModal';
import { Pagination } from '@/components/ui/Pagination';
import { useSyncStore } from '@/lib/sync';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const statuses = [
  'ACTIVE',
  'INACTIVE',
  'EXPIRING',
  'EXPIRED',
  'HOURS_EXCEED',
  'LOW_HOURS',
  'ARCHIVED',
];
const sortBy = [
  'name-ASC',
  'name-DESC',
  'startDate-ASC',
  'startDate-DESC',
  'contractedHours-ASC',
  'contractedHours-DESC',
];

export function ContractsPage() {
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<string>('default');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [hideInactiveExpired, setHideInactiveExpired] = useState<boolean>(true);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [contractToRenew, setContractToRenew] = useState<Contract | null>(null);
  const [syncedData, setSyncedData] = useState<DatabaseContract[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { lastContractsSync } = useSyncStore();

  const { t } = useTranslation();
  const { companies } = useCompanies({ limit: 100 });
  const {
    contracts,
    isLoading,
    isSaving,
    isSyncing,
    pagination,
    createContract,
    updateContract,
    deleteContract,
    renewContract,
    syncContracts,
  } = useContracts({
    page: currentPage,
    limit: 9,
    status: selectedStatus === 'all' ? undefined : selectedStatus,
    sortBy: selectedSort === 'default' ? undefined : selectedSort,
    hideInactiveExpired: hideInactiveExpired ? true : undefined,
    companyId: selectedCompany === 'all' ? undefined : selectedCompany,
  });

  const statusesOptions: SelectOption[] = [
    { value: 'all', content: t('common.all') },
    ...statuses.map((status) => ({
      value: status,
      content: t(`common.status.${status}`),
    })),
  ];

  const sortByOptions: SelectOption[] = [
    { value: 'default', content: t('common.defaultSort') },
    ...sortBy.map((s) => ({
      value: s,
      content: t(`common.sortBy.${s}`),
    })),
  ];

  const companiesOptions: SelectOption[] = [
    { value: 'all', content: t('common.all') },
    ...companies.map((company) => ({
      value: company.id,
      content: company.name,
    })),
  ];

  const handleEditContract = (contract: Contract) => {
    setSelectedContract(contract);
    setUpdateModalOpen(true);
  };

  const confirmDeleteContract = async (contractId: string) => {
    const success = await deleteContract(contractId);
    if (success) {
      setSelectedContract(null);
    }
  };

  const handleRenewContract = (contract: Contract) => {
    setContractToRenew(contract);
    setRenewModalOpen(true);
  };

  const handleSyncContracts = async () => {
    const data = await syncContracts();

    if (data) {
      setSyncedData(data);
      setSyncModalOpen(true);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, selectedSort, selectedCompany, hideInactiveExpired]);

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <CreateContractModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreate={createContract}
        isCreating={isSaving}
      />
      {selectedContract && (
        <UpdateContractModal
          open={updateModalOpen}
          onOpenChange={(open) => {
            setUpdateModalOpen(open);
            if (!open) setSelectedContract(null);
          }}
          contract={selectedContract}
          onUpdate={updateContract}
          isUpdating={isSaving}
        />
      )}
      {contractToRenew && (
        <RenewContractModal
          open={renewModalOpen}
          onOpenChange={(open) => {
            setRenewModalOpen(open);
            if (!open) setContractToRenew(null);
          }}
          contract={contractToRenew}
          onRenew={(data) => renewContract(contractToRenew.id, data)}
          isRenewing={isSaving}
        />
      )}
      <SyncContractsModal open={syncModalOpen} onOpenChange={setSyncModalOpen} data={syncedData} />

      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('contracts.title')}
        </Text>
        <div className="gap-md flex items-center">
          {lastContractsSync && (
            <Text variant="label" size="sm" className="text-neutral-muted">
              {t('contracts.lastSync')}{' '}
              {formatDistanceToNow(new Date(lastContractsSync), { addSuffix: true, locale: ptBR })}
            </Text>
          )}
          <Button
            variant="secondary"
            onClick={handleSyncContracts}
            disabled={isLoading}
            loading={isSyncing}
          >
            <RefreshCwIcon size={20} />
            {t('common.sync')}
          </Button>
          <Button onClick={() => setNewModalOpen(true)} disabled={isLoading}>
            <PlusIcon size={20} />
            {t('common.create')}
          </Button>
        </div>
      </header>

      <div>
        <div className="gap-xs flex items-center">
          <Select
            options={sortByOptions}
            placeholder={t('contracts.sortBy')}
            width={276}
            value={selectedSort === 'default' ? '' : selectedSort}
            onValueChange={(value) => setSelectedSort(value || 'default')}
          />
          <Select
            options={statusesOptions}
            placeholder={t('contracts.filterByStatus')}
            width={276}
            value={selectedStatus === 'all' ? '' : selectedStatus}
            onValueChange={(value) => setSelectedStatus(value || 'all')}
          />
          <Select
            options={companiesOptions}
            placeholder={t('contracts.filterByCompany')}
            width={276}
            value={selectedCompany === 'all' ? '' : selectedCompany}
            onValueChange={(value) => setSelectedCompany(value || 'all')}
          />
          <div className="flex items-center gap-2">
            <Switch checked={hideInactiveExpired} onCheckedChange={setHideInactiveExpired} />
            <Text variant="label" size="sm">
              {t('contracts.hideInactiveExpired')}
            </Text>
          </div>
        </div>

        <div className="mt-md gap-md grid grid-cols-3">
          {contracts.length > 0
            ? contracts.map((contract) => (
                <ContractCard
                  key={contract.id}
                  contract={contract}
                  onEdit={() => handleEditContract(contract)}
                  onDelete={() => confirmDeleteContract(contract.id)}
                  onRenew={() => handleRenewContract(contract)}
                />
              ))
            : !isLoading && (
                <div className="col-span-3 py-8 text-center">
                  <Text variant="label" className="text-neutral-muted">
                    {t('common.tableEmpty')}
                  </Text>
                </div>
              )}
        </div>
        <Pagination
          className="mt-lg"
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}
